import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { Warehouse } from 'src/app/models/common/warehouse.model';
import { DatabaseQueries, GetQuery, OrderQueries, SharedQueries } from 'src/app/models/shared/enums/get-query.enum';
import { WrapperResultGraphQL } from 'src/app/models/shared/types/wrapper-result-graphql.type';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { Color } from 'src/app/models/common/color.model';
import { PageInfo } from 'src/app/models/shared/types/page-info.type';
import { Ids } from 'src/app/models/common/ids.model';
import { CompanyStyle } from 'src/app/components/company/models/company-style.type';

@Component({
	selector: 'bt-add-product-modal',
	standalone: false,
	templateUrl: './add-product-modal.component.html',
	styleUrl: './add-product-modal.component.scss'
})
export class AddProductModalComponent {
	readonly #dialogRef = inject(MatDialogRef<AddProductModalComponent>);
	readonly #destroyRef = inject(DestroyRef);
	readonly #graphQLService = inject(GraphQLService);
	readonly #appService = inject(AppService);
	readonly #data = inject(MAT_DIALOG_DATA);
	readonly #PAGE_SIZE = 50;

	form: FormGroup;
	// Controllo usato solo per l'input di ricerca dell'autocomplete colori (non fa parte del form).
	colorSearchControl = new FormControl<string>('');

	warehouses: { data: Array<Warehouse>; pageInfo: PageInfo } = { data: [], pageInfo: { endCursor: null, hasNextPage: false, hasPreviusPage: false, startCursor: null } };
	styles: { data: Array<CompanyStyle>; page: number; pageSize: number } = { data: [], page: 0, pageSize: 50 };
	colors: { data: Array<Color>; pageInfo: PageInfo } = { data: [], pageInfo: { endCursor: null, hasNextPage: false, hasPreviusPage: false, startCursor: null } };

	constructor() {
		this.setForm();
	}

	inViewportActionWarehouse(ev: { isVisible: boolean }, item: Warehouse) {
		if (!ev.isVisible) return;
		const index = this.warehouses.data.findIndex((el) => el === item);
		if (index === this.warehouses.data.length - 1 && this.warehouses.pageInfo.hasNextPage) {
			this.getWarehouses();
		}
	}

	inViewportActionStyles(ev: { isVisible: boolean }, item: CompanyStyle) {
		if (!ev.isVisible) return;
		const index = this.styles.data.findIndex((el) => el === item);
		if (index === this.styles.data.length - 1 && this.styles.data.length % this.#PAGE_SIZE === 0) {
			this.getStyles(this.styles.page + 1);
		}
	}

	inViewportActionColors(ev: { isVisible: boolean }, item: Color) {
		if (!ev.isVisible) return;
		const index = this.colors.data.findIndex((el) => el === item);
		if (index === this.colors.data.length - 1 && this.colors.pageInfo.hasNextPage) {
			this.getColors();
		}
	}

	//Display Data
	displayWarehouse(warehouse: Warehouse): string {
		return warehouse?.fullName || '';
	}

	displayStyle(style: CompanyStyle): string {
		return style?.fullName || '';
	}

	displayColor(color: Color): string {
		return color?.fullName || '';
	}

	/** Restituisce i colori ancora non selezionati (per l'elenco dell'autocomplete). */
	get colorsAvailable(): Color[] {
		const selected = (this.form.get('colors')?.value as Color[]) ?? [];
		const ids = new Set(selected.map((c) => c.colorId));
		return this.colors.data.filter((c) => !ids.has(c.colorId));
	}

	onColorSelected(color: Color): void {
		const control = this.form.get('colors');
		const current: Color[] = control?.value ?? [];
		if (current.some((c) => c.colorId === color.colorId)) return;
		control?.setValue([...current, color]);
		this.colorSearchControl.setValue('', { emitEvent: false });
	}

	removeColor(color: Color): void {
		const control = this.form.get('colors');
		const current: Color[] = control?.value ?? [];
		control?.setValue(current.filter((c) => c.colorId !== color.colorId));
	}

	private setForm(): void {
		const colorsValidator = (ctrl: AbstractControl) => (Array.isArray(ctrl.value) && ctrl.value.length > 0 ? null : { required: true });

		this.form = new FormGroup({
			warehouse: new FormControl<string | Warehouse>('', { validators: [Validators.required] }),
			style: new FormControl<string | CompanyStyle>({ value: '', disabled: true }, { validators: [Validators.required] }),
			colors: new FormControl<Color[]>({ value: [], disabled: true }, { validators: [colorsValidator] })
		});
		this.colorSearchControl.disable();

		this.setupWarehouseAutocomplete();
		this.setupStyleAutocomplete();
		this.setupColorAutocomplete();
	}

	//Setup Autocomplete
	private setupWarehouseAutocomplete(): void {
		this.form
			.get('warehouse')
			.valueChanges.pipe(startWith(''), debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
			.subscribe((value) => {
				if (typeof value === 'string') {
					this.getWarehouses(true, value);
					this.form.get('style').setValue(undefined);
					this.form.get('style').disable();
				}
				if (typeof value === 'object') {
					this.getStyles(0);
					this.form.get('style').enable();
				}
			});
	}

	private setupStyleAutocomplete(): void {
		this.form
			.get('style')
			.valueChanges.pipe(startWith(''), debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
			.subscribe((value) => {
				if (typeof value === 'string') this.getStyles(0, value);
				if (typeof value === 'object') this.getColors(true);

				if (typeof value === 'string') {
					this.getWarehouses(true, value);
					this.form.get('colors').setValue([]);
					this.form.get('colors').disable();
					this.colorSearchControl.disable();
				}
				if (typeof value === 'object') {
					this.getStyles(0);
					this.form.get('colors').enable();
					this.colorSearchControl.enable();
				}
			});
	}

	private setupColorAutocomplete(): void {
		this.colorSearchControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
			if (typeof value === 'string') this.getColors(true, value);
		});
	}

	//Get Data
	private getWarehouses(firstOpen?: boolean, search?: string) {
		let query = '';
		let filters = {};

		if (this.#data.warehouse) {
			query = DatabaseQueries.GET_WAREHOUSES;
			filters = {
				first: 50,
				after: this.warehouses.pageInfo.endCursor,
				search: search ?? '',
				companyId: this.#appService.companyId,
				warehouseIds: [this.#data.warehouse.id]
			};
		} else {
			query = OrderQueries.GET_WAREHOUSES_NO_IDS;
			filters = {
				first: 50,
				after: this.warehouses.pageInfo.endCursor,
				search: search ?? '',
				companyId: this.#appService.companyId
			};
		}

		if (firstOpen) this.warehouses.pageInfo.endCursor = null;
		this.#graphQLService
			.getData<any>(query as GetQuery, filters)
			.pipe(
				map((res) => res['data']['warehouses']),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe({
				next: (data: WrapperResultGraphQL<Warehouse>) => {
					const newData = data.nodes.map((el) => {
						const element: Warehouse = {
							...el,
							fullName: `${el.code.trim()} - ${el.description.trim()}`
						};
						return element;
					});
					this.warehouses.data = this.warehouses.pageInfo.endCursor ? this.warehouses.data.concat(newData) : newData;

					this.warehouses.pageInfo = { ...data.pageInfo };

					if (this.#data.warehouse) {
						this.form.get('warehouse').setValue(this.#data.warehouse);
						this.form.get('warehouse').disable();
						this.form.get('style').enable();
						this.getStyles(0);
					}
				}
			});
	}

	private getStyles(page: number, search?: string) {
		if (typeof this.form.get('warehouse')?.value !== 'object') return;
		const warehouseId = this.form.get('warehouse').value.id;
		this.styles.page = page;

		const filters = {
			companyId: this.#appService.companyId,
			paginationPage: this.styles.page ?? 0,
			paginationSize: this.styles.pageSize,
			search: search ?? '',
			notAssigned: false,
			brandIds: [],
			categoryIds: [],
			photographersIds: [],
			warehousesIds: [new Ids(warehouseId)],
			styleGroupIds: [],
			styleDescriptionIds: [],
			collectionIds: [],
			purposeIds: [],
			teamIds: [],
			suggestedSeasonIds: []
		};

		this.#graphQLService
			.getData<CompanyStyle>(DatabaseQueries.GET_COMPANY_STYLES, filters)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res) => res['data']['companyStyles'])
			)
			.subscribe({
				next: (data: WrapperResultGraphQL<any>) => {
					this.styles.data = !this.styles.page
						? data.nodes[0].datasource.map((el) => ({ ...el }))
						: this.styles.data.concat(data.nodes[0].datasource.map((el) => ({ ...el })));

					this.form.get('colors')?.setValue([]);
				}
			});
	}

	private getColors(firstOpen?: boolean, search?: string) {
		if (firstOpen) this.colors.pageInfo.endCursor = null;
		if (typeof this.form.get('style')?.value !== 'object') return;
		const modelId = this.form.get('style').value.modelId;

		const filter = {
			first: 50,
			after: this.colors.pageInfo.endCursor,
			modelId,
			search: search ?? ''
		};

		this.#graphQLService
			.getData<Color>(SharedQueries.GET_COLORS_BY_MODEL, filter)
			.pipe(
				map((res) => res['data']['colors']),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe({
				next: (data: WrapperResultGraphQL<any>) => {
					this.colors.data = !this.colors.pageInfo.endCursor
						? data.nodes.map((el) => ({
								colorId: el.id,
								colorCode: el.code,
								colorName: el.finalColorName,
								fullName: `${el.code}${el.finalColorName.trim() ? ' - ' + el.finalColorName.trim() : ''}`
							}))
						: this.colors.data.concat(data.nodes.map((el) => ({ ...el })));
					this.colors.pageInfo = { ...data.pageInfo };
				}
			});
	}

	public onClose(confirm: boolean) {
		if (confirm) this.#dialogRef.close(this.form.value);
		else this.#dialogRef.close(null);
	}
}
